import { AnswerModel } from '@/components/decision-tree/hooks';
import { getMaskedNumericValue } from '@/utils';
import { ChangeEvent, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { HeightAnswer, HeightValidation, Measurement } from './types';

type ErrorType = { value: number; message: string };

type HeightControllerProps = {
  id: string;
  validationValues: HeightValidation;
  saveAnswer: (payload: AnswerModel) => void;
  defaultValue?: HeightAnswer;
  feet: Measurement;
  inches: Measurement;
};

export default function useHeightController({
  id,
  validationValues,
  saveAnswer,
  defaultValue,
  feet,
  inches,
}: HeightControllerProps) {
  const form = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: { feet: defaultValue?.feet ?? '', inches: defaultValue?.inches ?? '' },
  });

  const feetValidationOptions: {
    min?: ErrorType;
    max?: ErrorType;
  } = {};
  if (validationValues?.feet.min)
    feetValidationOptions.min = {
      value: validationValues.feet.min,
      message: 'The value you entered seems unusually low',
    };
  if (validationValues?.feet.max)
    feetValidationOptions.max = {
      value: validationValues.feet.max,
      message: 'The value you entered seems unusually high',
    };

  const inchesValidationOptions: {
    min?: ErrorType;
    max?: ErrorType;
  } = {};
  if (validationValues?.inches.min)
    inchesValidationOptions.min = {
      value: validationValues.inches.min,
      message: 'The value you entered seems unusually low',
    };
  if (validationValues?.inches.max)
    inchesValidationOptions.max = {
      value: validationValues.inches.max,
      message: 'The value you entered seems unusually high',
    };

  const [answers, setAnswers] = useState<HeightAnswer>({ type: 'height', feet: null, inches: null });
  useEffect(() => setAnswers({ type: 'height', feet: null, inches: null }), [id]);

  const handleChange = form.handleSubmit(({ feet, inches }) => {
    const newAnswer = { ...answers, feet: Number(feet), inches: Number(inches) };
    setAnswers(newAnswer);
    saveAnswer(newAnswer);
  });

  const onInputFeetFn = ({ currentTarget }: ChangeEvent<HTMLInputElement>) => {
    form.setValue('feet', getMaskedNumericValue(currentTarget.value, feet.trailingMask ?? ''));
    if (form.formState.errors.feet) handleChange();
  };

  const onInputInchesFn = ({ currentTarget }: ChangeEvent<HTMLInputElement>) => {
    form.setValue('inches', getMaskedNumericValue(currentTarget.value, inches.trailingMask ?? ''));
    if (form.formState.errors.inches) handleChange();
  };

  const resetSavedAnswer = () => {
    setAnswers({ type: 'height', feet: null, inches: null });
    saveAnswer({ type: 'height', feet: null, inches: null });
  };

  const onBlurFeetFn = ({ currentTarget }: ChangeEvent<HTMLInputElement>) => {
    resetSavedAnswer();
    form.setValue('feet', getMaskedNumericValue(currentTarget.value, feet.trailingMask ?? ''));
    handleChange();
  };

  const onBlurInchesFn = ({ currentTarget }: ChangeEvent<HTMLInputElement>) => {
    resetSavedAnswer();
    form.setValue('inches', getMaskedNumericValue(currentTarget.value, inches.trailingMask ?? ''));
    handleChange();
  };

  return {
    onInputFeetFn,
    onInputInchesFn,
    form,
    feetValidationOptions,
    inchesValidationOptions,
    onBlurFeetFn,
    onBlurInchesFn,
  };
}
